'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';

import { en as DEFAULT_LOCALE } from '../locales';

export interface LocaleText {
  // Common action labels
  or: string;
  to: string;

  ok: string;
  with: string;

  save: string;
  // Common authentication labels
  email: string;
  close: string;
  alert: string;

  cancel: string;
  delete: string;
  passkey: string;
  confirm: string;

  loading: string;
  username: string;
  password: string;
  editLabel: string;
  createLabel: string;
  deleteLabel: string;
  signInSubtitle: string;
  signInRememberMe: string;
  editErrorMessage: string;
  reloadButtonLabel: string;
  deleteCancelLabel: string;

  // Account
  accountSignInLabel: string;
  createErrorMessage: string;
  editSuccessMessage: string;
  deleteConfirmTitle: string;
  deleteConfirmLabel: string;
  deleteErrorMessage: string;
  deletedItemMessage: string;
  accountSignOutLabel: string;
  accountPreviewTitle: string;
  // CRUD
  createNewButtonLabel: string;
  createSuccessMessage: string;
  deleteConfirmMessage: string;
  deleteSuccessMessage: string;
  // AccountPreview
  accountPreviewIconButtonLabel: string;
  providerSignInTitle: (provider: string) => string;
  // SignInPage
  signInTitle: ((brandingTitle?: string) => string) | string;
}

export interface LocalizationProviderProps {
  children?: React.ReactNode;
  /**
   * Locale for components texts
   */
  localeText?: Partial<LocaleText>;
}

export const LocalizationContext = React.createContext<Partial<LocaleText>>({});

const LocalizationProvider = function LocalizationProvider(
  props: LocalizationProviderProps
) {
  const { children, localeText: propsLocaleText } = props;

  const theme = useTheme();
  // @ts-ignore
  const themeLocaleText =
    theme?.components?.MuiLocalizationProvider?.defaultProps?.localeText;

  const defaultLocaleText =
    DEFAULT_LOCALE.components.MuiLocalizationProvider.defaultProps.localeText;

  /* The order of overrides is:
   * 1. The `localeText` prop of the `AppProvider` supersedes
   * 2. The localeText provided as an argument to the `createTheme` function, which supersedes
   * 3. The default locale text
   */

  const localeText = React.useMemo(
    () => ({ ...defaultLocaleText, ...themeLocaleText, ...propsLocaleText }),
    [defaultLocaleText, themeLocaleText, propsLocaleText]
  );

  return (
    <LocalizationContext.Provider value={localeText}>
      {children}
    </LocalizationContext.Provider>
  );
};

LocalizationProvider.propTypes /* remove-proptypes */ = {
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * Locale for components texts
   */
  localeText: PropTypes.object,
} as any;

export { LocalizationProvider };
/**
 *
 * Demos:
 *
 * - [Sign-in Page](https://mui.com/toolpad/core/react-sign-in-page/)
 *
 * API:
 *
 * - [LocalizationProvider API](https://mui.com/toolpad/core/api/localization-provider)
 */
export function useLocaleText() {
  return React.useContext(LocalizationContext);
}
